import { stableVaultContractConfig } from "@config/contractsConfig";
import { formatEther } from "@ethersproject/units";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo } from "react";
import { useIdle, useInterval } from "react-use";
import { parseEther } from "viem";
import { useAccount, useReadContracts } from "wagmi";
import useDappChain from "./useDappChain";
import useDappConfig from "./useDappConfig";
import useStableAssetExchangeRate from "./useStableAssetExchangeRate";

const POLLING_INTERVAL = 10000;

const useStableVaultClaims = () => {
  const isIdle = useIdle(150e3);
  const { isConnected, address } = useAccount();
  const { chain } = useDappChain();
  const { stableVaultConfig } = useDappConfig();
  const stableAssetExchangeRate = useStableAssetExchangeRate();

  const pathname = usePathname();

  const isStableVaultPage = useMemo(() => {
    return pathname.includes("/stable-vault");
  }, [pathname]);

  const outputTokens = useMemo(() => {
    const tokens = stableVaultContractConfig[chain.id]?.tokens;
    if (!tokens) return [];
    return Object.keys(tokens).filter(
      (key) => key !== stableVaultConfig?.xToken?.symbol
    );
  }, [chain.id, stableVaultConfig?.xToken?.symbol]);

  const contractCalls = useMemo(() => {
    if (!address || outputTokens.length === 0) return [];

    const calls: any[] = [];

    outputTokens.forEach((tokenSymbol) => {
      const tokenConfig =
        stableVaultContractConfig[chain.id]?.tokens?.[tokenSymbol];
      if (tokenConfig) {
        calls.push({
          ...stableVaultContractConfig[chain.id]?.interactionPool,
          functionName: "getPendingRedeemForAsset",
          args: [tokenConfig.address, address],
        });

        calls.push({
          ...stableVaultContractConfig[chain.id]?.interactionPool,
          functionName: "getClaimableRedeemForAsset",
          args: [tokenConfig.address, address],
        });
      }
    });

    return calls;
  }, [address, outputTokens, chain.id]);

  const {
    data: vaultData,
    refetch,
    isLoading,
    isFetching,
  } = useReadContracts({
    contracts: contractCalls,
    query: {
      enabled: !isIdle && !!address && contractCalls.length > 0,
    },
  });

  const processedClaims = useMemo(() => {
    if (!vaultData || !address || outputTokens.length === 0) return [];

    const claims: any[] = [];

    // Get exchange rate for converting sbUSD to USDC
    const exchangeRate = stableAssetExchangeRate?.toERC20
      ? Number(stableAssetExchangeRate.toERC20)
      : 1;

    outputTokens.forEach((tokenSymbol, tokenIndex) => {
      const pendingIndex = tokenIndex * 2;
      const claimableIndex = tokenIndex * 2 + 1;

      const pendingData = vaultData[pendingIndex]?.result as any;
      const claimableData = vaultData[claimableIndex]?.result as any;

      if (pendingData && pendingData.shares && pendingData.shares > 0n) {
        const requestTime = Number(pendingData.requestTime || 0) * 1000;
        const claimableAt = requestTime + 3 * 24 * 60 * 60 * 1000; // 3 days later

        // Convert shares (sbUSD) to USDC using exchange rate
        const sharesInEther = Number(formatEther(pendingData.shares));
        const claimableAmountInUsdc = sharesInEther * exchangeRate;

        // Format to avoid scientific notation (parseEther doesn't accept it)
        const formattedAmount = claimableAmountInUsdc.toFixed(18);
        const claimableAmountInWei = parseEther(formattedAmount);

        claims.push({
          id: `pending-${tokenSymbol}-${requestTime}`,
          tokenSymbol,
          type: "pending",
          shares: pendingData.shares,
          claimableAmount: claimableAmountInWei,
          requestTime: requestTime || Date.now(),
          claimableAt,
          isClaimable: false,
          receiver: address,
        });
      }

      if (claimableData && claimableData.assets && claimableData.assets > 0n) {
        claims.push({
          id: `claimable-${tokenSymbol}-${Date.now()}`,
          tokenSymbol,
          type: "claimable",
          shares: claimableData.shares || 0n,
          claimableAmount: claimableData.assets || 0n,
          claimableAt: Date.now(),
          isClaimable: true,
          receiver: address,
        });
      }
    });

    return claims.sort((a, b) => b.claimableAt - a.claimableAt);
  }, [vaultData, address, outputTokens, stableAssetExchangeRate]);

  const fetchClaims = useCallback(() => {
    if (address && contractCalls.length > 0) {
      refetch();
    }
  }, [address, contractCalls.length, refetch]);

  useEffect(() => {
    if (isConnected && address) {
      fetchClaims();
    }
  }, [isConnected, address, fetchClaims]);

  useInterval(() => {
    if (!isIdle && isStableVaultPage) {
      fetchClaims();
    }
  }, POLLING_INTERVAL);

  return {
    claims: processedClaims,
    isLoading: (isLoading || isFetching) && isConnected && !vaultData,
  };
};

export default useStableVaultClaims;
